import React, { useEffect, useState } from 'react';
import { liveCountForMission } from '../utils/dashboard/missionsModel';
import { canEditMissionBoard, hasCommandFeature, COMMAND_FEATURE_PERMS } from '../utils/permissionControl';

/**
 * Horizontal mission tabs above MissionKanbanPanel.
 * Active tab is visually loud; name editable (pencil / double-click).
 */
export default function MissionTabsBar({
  missions = [],
  activeMissionId = null,
  liveMembers = [],
  onSelectMission,
  onCreateMission,
  onRenameMission,
  onCloseMission,
  onToggleMissionRecordTracks
}) {
  const list = Array.isArray(missions) ? missions : [];
  const canEditBoard = canEditMissionBoard();
  const canRecordTracks = canEditBoard && hasCommandFeature(COMMAND_FEATURE_PERMS.MEMBER_COMMAND);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState('');

  useEffect(() => {
    if (editingId && !list.some((m) => m.id === editingId)) {
      setEditingId(null);
      setDraft('');
    }
  }, [list, editingId]);

  const beginEdit = (m) => {
    setEditingId(m.id);
    setDraft(String(m.name || ''));
  };

  const commitEdit = () => {
    if (!editingId) return;
    const name = String(draft || '').trim();
    if (name) onRenameMission?.(editingId, name);
    setEditingId(null);
    setDraft('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft('');
  };

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 8,
        marginTop: 8,
        marginBottom: 4,
        padding: '8px 6px',
        background: 'linear-gradient(180deg, #eef5ff 0%, #f7faff 100%)',
        border: '1px solid #c5d8f5',
        borderRadius: 10
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: '#0d47a1', marginRight: 4, whiteSpace: 'nowrap' }}>
        {'\u4efb\u52a1'}
      </div>
      {list.map((m) => {
        const active = m.id === activeMissionId;
        const count = liveCountForMission(m, liveMembers);
        const editing = editingId === m.id;
        return (
          <div
            key={m.id}
            role="tab"
            aria-selected={active}
            onClick={() => {
              if (!editing) onSelectMission?.(m.id);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              maxWidth: '100%',
              height: 34,
              padding: active ? '0 12px' : '0 10px',
              borderRadius: 8,
              cursor: 'pointer',
              border: active ? '2px solid #0d47a1' : '1px solid #b0bec5',
              background: active ? '#1565c0' : '#fff',
              color: active ? '#fff' : '#37474f',
              boxShadow: active ? '0 2px 8px rgba(13,71,161,0.35)' : 'none',
              fontWeight: active ? 700 : 500,
              fontSize: active ? 14 : 12,
              lineHeight: 1.2
            }}
          >
            {editing ? (
              <input
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitEdit();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    cancelEdit();
                  }
                }}
                onBlur={commitEdit}
                style={{
                  width: Math.min(160, Math.max(72, (draft || '').length * 12 + 24)),
                  height: 24,
                  border: '1px solid #90caf9',
                  borderRadius: 4,
                  padding: '0 6px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#0d47a1'
                }}
              />
            ) : (
              <span
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  beginEdit(m);
                }}
                title={'\u53cc\u51fb\u6216\u70b9\u94c5\u7b14\u91cd\u547d\u540d'}
                style={{
                  maxWidth: 140,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {m.name || '\u4efb\u52a1'}
              </span>
            )}
            <span
              style={{
                minWidth: 18,
                height: 18,
                padding: '0 5px',
                borderRadius: 9,
                fontSize: 11,
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: active ? 'rgba(255,255,255,0.25)' : '#e3f2fd',
                color: active ? '#fff' : '#1565c0'
              }}
            >
              {count}
            </span>
            {!editing && canRecordTracks && (
              <button
                type="button"
                role="switch"
                aria-checked={m.recordTracks === true}
                title={m.recordTracks === true
                  ? '\u5173\u95ed\u4efb\u52a1\u8bb0\u5f55\u8f68\u8ff9'
                  : '\u5f00\u542f\u4efb\u52a1\u8bb0\u5f55\u8f68\u8ff9\uff08\u81ea\u52a8\u7eed\u753b+\u5355\u5175\u5bc6\u70b9\uff09'}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleMissionRecordTracks?.(m.id, !(m.recordTracks === true));
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  flexShrink: 0,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: '0 2px',
                  fontSize: 11,
                  color: m.recordTracks === true ? (active ? '#fff' : '#2e7d32') : (active ? '#bbdefb' : '#8a93a0'),
                  fontWeight: m.recordTracks === true ? 600 : 400,
                  lineHeight: 1
                }}
              >
                <span>{'\u8f68\u8ff9'}</span>
                <span
                  aria-hidden="true"
                  style={{
                    position: 'relative',
                    width: 28,
                    height: 16,
                    borderRadius: 8,
                    background: m.recordTracks === true ? (active ? 'rgba(255,255,255,0.55)' : '#2e7d32') : (active ? 'rgba(255,255,255,0.22)' : '#cfd5de'),
                    transition: 'background 0.15s ease',
                    flexShrink: 0
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: 2,
                      left: m.recordTracks === true ? 14 : 2,
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      background: '#fff',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                      transition: 'left 0.15s ease'
                    }}
                  />
                </span>
              </button>
            )}
            {canEditBoard && !editing && (
              <button
                type="button"
                title={'\u91cd\u547d\u540d'}
                onClick={(e) => {
                  e.stopPropagation();
                  beginEdit(m);
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: active ? '#e3f2fd' : '#607d8b',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: 13,
                  lineHeight: 1
                }}
              >
                {'\u270e'}
              </button>
            )}
            {canEditBoard && !active && (
              <button
                type="button"
                title={'\u5173\u95ed\u6b64\u4efb\u52a1'}
                onClick={(e) => {
                  e.stopPropagation();
                  onCloseMission?.(m.id);
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#90a4ae',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: 14,
                  lineHeight: 1,
                  fontWeight: 700
                }}
              >
                {'\u00d7'}
              </button>
            )}
          </div>
        );
      })}
      {canEditBoard && (
      <button
        type="button"
        onClick={() => onCreateMission?.()}
        style={{
          height: 34,
          padding: '0 12px',
          borderRadius: 8,
          border: '1px dashed #1565c0',
          background: '#fff',
          color: '#1565c0',
          fontSize: 13,
          fontWeight: 700,
          cursor: 'pointer',
          whiteSpace: 'nowrap'
        }}
      >
        {'+ \u65b0\u5efa\u4efb\u52a1'}
      </button>
      )}
    </div>
  );
}
